// src/pages/DashboardNino.js

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import { toast } from 'react-toastify';
import Confetti from 'react-confetti';
// --- CORRECCIÓN AQUÍ: Aseguramos que FaTrophy esté importado ---
import { FaTrophy } from 'react-icons/fa'; 
// --------------------------------------------------------------
import Card from '../components/Card';
import Misiones from '../components/Misiones';
import TiendaRecompensas from '../components/TiendaRecompensas';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

// --- Componente para la barra de progreso ---
function BarraProgresoRecompensas({ puntos, recompensas }) {
  
  // 1. CASO: No hay recompensas creadas
  if (recompensas.length === 0) {
    return (
      <Card>
        <h2 className="text-xl font-bold text-center text-gray-700">¡Bienvenido a tu aventura! 🗺️</h2>
        <p className="text-center text-gray-500 mt-2">
          La tienda de premios está vacía. ¡Dile a tus padres que añadan algunas recompensas!
        </p>
      </Card>
    );
  }

  const proximaRecompensa = recompensas
    .sort((a, b) => a.puntos_necesarios - b.puntos_necesarios)
    .find(r => r.puntos_necesarios > puntos);

  // 2. CASO: Tiene puntos suficientes para TODO
  if (!proximaRecompensa) {
    return (
      <Card>
        <h2 className="text-xl font-bold text-center text-principal">¡Eres una superestrella! 🌟</h2>
        <p className="text-center text-gray-600">Tienes puntos suficientes para canjear cualquier premio de la tienda.</p>
      </Card>
    );
  }

  // 3. CASO NORMAL: Barra de progreso
  const progreso = Math.floor((puntos / proximaRecompensa.puntos_necesarios) * 100);
  const puntosFaltantes = proximaRecompensa.puntos_necesarios - puntos;

  return (
    <Card className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold text-gray-700">Próxima Meta: <span className="text-principal">{proximaRecompensa.titulo}</span></h3>
        <span className="font-bold text-acento flex items-center gap-1"><FaTrophy /> {proximaRecompensa.puntos_necesarios} Puntos</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-6">
        <div 
          className="bg-acento h-6 rounded-full text-center text-white font-bold flex items-center justify-center transition-all duration-500"
          style={{ width: `${progreso}%` }}
        >
          {progreso > 10 && `${progreso}%`}
        </div>
      </div>
      <p className="text-center text-sm text-gray-600 mt-2">¡Ánimo! Solo te faltan <span className="font-bold">{puntosFaltantes}</span> puntos más.</p>
    </Card>
  );
}

// --- Componente Principal del Dashboard del Niño ---
function DashboardNino() {
  const [tareas, setTareas] = useState([]);
  const [recompensas, setRecompensas] = useState([]);
  const [puntos, setPuntos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      const [tareasRes, perfilRes, recompensasRes] = await Promise.all([
        apiClient.get('/tareas/'),
        apiClient.get('/perfil/'),
        apiClient.get('/recompensas/')
      ]);
      const tareasNuevas = tareasRes.data;
      setTareas(prevTareas => {
        if (prevTareas.length > 0) {
          const tareaRecienAprobada = tareasNuevas.find((n) => n.aprobada && !prevTareas.find((v) => v.id === n.id)?.aprobada);
          if (tareaRecienAprobada) {
              toast.success(`¡Ganaste ${tareaRecienAprobada.puntos} puntos! 🥳`);
              setShowConfetti(true);
              setTimeout(() => setShowConfetti(false), 8000);
          }
        }
        return tareasNuevas;
      });
      setRecompensas(recompensasRes.data);
      setPuntos(perfilRes.data.puntos);
    } catch (error) {
      if (error.response?.status !== 401) {
        // toast.error("No se pudieron cargar los datos."); // Opcional: comentar para evitar spam de errores si falla la red
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // --- Handlers ---
  const handleCompletarTarea = async (tareaId) => {
    try {
      await apiClient.post(`/tareas/${tareaId}/completar/`);
      toast.info('¡Tarea enviada para aprobación!');
      setTareas(prev => prev.map(t => t.id === tareaId ? { ...t, completada: true } : t));
    } catch (error) { toast.error('Error al completar la tarea.'); }
  };

  const handleCompletarHabito = async (tareaId) => {
    try {
      // 1. Obtenemos la fecha local del navegador en formato YYYY-MM-DD
      // El truco de 'en-CA' suele dar formato YYYY-MM-DD, pero para ser más robustos:
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const fechaLocal = `${year}-${month}-${day}`;

      // 2. Enviamos la fecha en la petición
      await apiClient.post(`/tareas/${tareaId}/completar_habito/`, { 
        fecha: fechaLocal 
      });

      toast.info('¡Hábito completado para hoy! Esperando aprobación.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudo completar el hábito.');
    }
  };

  const handleCanjearRecompensa = async (recompensa) => {
    if (window.confirm(`¿Canjear "${recompensa.titulo}" por ${recompensa.puntos_necesarios} puntos?`)) {
      try {
        const response = await apiClient.post(`/recompensas/${recompensa.id}/canjear/`);
        setPuntos(response.data.usuario.puntos);
        toast.success(response.data.status);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      } catch (error) { toast.error(error.response?.data?.error || 'No se pudo canjear.'); }
    }
  };

  if (loading) return <div className="p-8 text-center text-xl">Cargando tu mundo mágico...</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {showConfetti && <Confetti recycle={false} numberOfPieces={400} />}
      
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <Card>
          <span className="text-2xl font-bold text-acento">⭐ {puntos} Puntos</span>
        </Card>
      </div>
      
      <BarraProgresoRecompensas puntos={puntos} recompensas={recompensas} />

      <Tabs className="mt-12">
        <TabList>
          <Tab>Mis Misiones</Tab>
          <Tab>Tienda de Recompensas</Tab>
        </TabList>

        <TabPanel>
          <div className="mt-6">
            <Misiones 
              tareas={tareas}
              onCompletarTarea={handleCompletarTarea}
              onCompletarHabito={handleCompletarHabito}
            />
          </div>
        </TabPanel>
        <TabPanel>
          <div className="mt-6">
            <TiendaRecompensas
              recompensas={recompensas}
              puntos={puntos}
              onCanjearRecompensa={handleCanjearRecompensa}
            />
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

export default DashboardNino;